import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Image, 
  Clock, 
  Download, 
  QrCode, 
  Share2, 
  Settings, 
  ArrowLeft, 
  Eye, 
  Edit2, 
  Trash2,
  Grid,
  Info,
  ChevronRight,
  Upload,
  FileImage,
  Lock,
  Link as LinkIcon,
  Activity,
  Check,
  X,
  Loader2,
  Mail,
  AlertTriangle,
  Copy
} from 'lucide-react';
import Button from '../../../components/ui/Button';
import QRCodeModal from '../../../components/modals/QRCodeModal';
import UploadPhoto from '../../../components/modals/UploadPhoto';
import { Event } from '../../../types/event';
import { Photo } from '../../../types/photos';
import { toast } from 'react-toastify';
import { 
  useGetEventDetailsQuery, 
  useGetEventPhotosQuery,
  useDeleteEventMutation,
  useRequestPhotosZipMutation
} from '../../../store/services/api';

const EventDetail = () => {
  const { url } = useParams<{ url: string }>();
  const navigate = useNavigate();

  // RTK Query hooks
  const { 
    data: event,
    isLoading: isLoadingEvent,
    error: eventError
  } = useGetEventDetailsQuery(url || '', { skip: !url });

  const {
    data: photos = [],
    isLoading: isLoadingPhotos,
    refetch: refetchPhotos
  } = useGetEventPhotosQuery(url || '', { skip: !url });

  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [requestZip] = useRequestPhotosZipMutation();

  // Local state
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isImageViewOpen, setIsImageViewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [visiblePhotos, setVisiblePhotos] = useState(12);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [isDownloadingOne, setIsDownloadingOne] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [timeLeftString, setTimeLeftString] = useState<string>('');
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Time left calculation
  useEffect(() => {
    if (!event) return;

    const updateTimeLeft = () => {
      if (!event || !event.expires_at) {
        setTimeLeftString('Unlimited');
        return;
      }

      const expiry = new Date(event.expires_at);
      const now = new Date();
      
      if (now > expiry) {
        setTimeLeftString('Expired');
        return;
      }
      
      const diffInMs = expiry.getTime() - now.getTime();
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      
      if (diffInDays > 30) {
        const diffInMonths = Math.floor(diffInDays / 30);
        setTimeLeftString(`${diffInMonths} month${diffInMonths > 1 ? 's' : ''}`);
      } else {
        setTimeLeftString(`${diffInDays} day${diffInDays !== 1 ? 's' : ''}`);
      }
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 60000); // update every minute

    return () => clearInterval(interval);
  }, [event]);

  const handleLoadMore = () => {
    if (!photos) return;
    setVisiblePhotos(prev => Math.min(prev + 12, photos.length));
  };

  const handleImageView = (photo: Photo) => {
    setSelectedImage(photo.public_url);
    setIsImageViewOpen(true);
  };

  const handleDownloadAll = async () => {
    if (!photos || photos.length === 0 || !event || !event.id) return;
    
    // Check last download time
    const lastDownloadKey = `last_download_${event.id}`;
    const lastDownload = localStorage.getItem(lastDownloadKey);
    
    if (lastDownload) {
      const lastDownloadTime = new Date(lastDownload).getTime();
      const now = new Date().getTime();
      const hoursSinceLastDownload = (now - lastDownloadTime) / (1000 * 60 * 60);
      
      if (hoursSinceLastDownload < 24) {
        const remainingHours = Math.ceil(24 - hoursSinceLastDownload);
        toast.error(`You can request another download in ${remainingHours} hours`);
        return;
      }
    }
    
    setIsDownloading(true);
    
    try {
      const response = await requestZip(event.id).unwrap();
      
      setIsDownloading(false);
      setDownloadComplete(true);
      
      // Save last download time
      localStorage.setItem(lastDownloadKey, new Date().toISOString());
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setDownloadComplete(false);
      }, 5000);
      
      toast.success(response.message || "Download request submitted successfully. You'll receive an email when it's ready.");
    } catch (error) {
      console.error("Error requesting download:", error);
      toast.error("Failed to start download process. Please try again later.");
      setIsDownloading(false);
    }
  };

  const handleDownloadSingle = (photoId: number) => {
    setIsDownloadingOne(photoId);
    
    const photo = photos.find(p => p.id === photoId);
    if (!photo) {
      setIsDownloadingOne(null);
      return;
    }
    
    try {
      fetch(photo.public_url)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.setAttribute('download', photo.file_name || `photo-${photoId}.jpg`);
          anchor.setAttribute('rel', 'noopener noreferrer');
          anchor.style.display = 'none';
          document.body.appendChild(anchor);
          anchor.click();
          
          setTimeout(() => {
            document.body.removeChild(anchor);
            URL.revokeObjectURL(blobUrl);
          }, 100);
        });
    } catch (error) {
      console.error('Download failed:', error);
    }
    
    setTimeout(() => {
      setIsDownloadingOne(null);
    }, 1500);
  };

  const handleDeleteConfirm = async () => {
    if (!event || !event.url) return;
    
    try {
      await deleteEvent(event.url).unwrap();
      navigate('/dashboard/events');
    } catch (error) {
      console.error('Error deleting event:', error);
      setIsDeleteModalOpen(false);
    }
  };

  const handleCopyLink = () => {
    if (!event || !event.url) return;
    
    const shareUrl = `https://ourphotos.co/e/${event.url}`;
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    
    setTimeout(() => {
      setLinkCopied(false);
    }, 2000);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (isLoadingEvent) {
    return (
      <div className="max-w-full mx-auto px-4">
        <div className="bg-white p-8 rounded-lg border border-gray-100 text-center my-8">
          <Loader2 className="w-8 h-8 text-[#FF4444] mx-auto animate-spin mb-4" />
          <h3 className="text-base font-medium mb-1">Loading event details...</h3>
          <p className="text-sm text-gray-500">Please wait while we fetch the event information.</p>
        </div>
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="max-w-full mx-auto px-4">
        <div className="bg-white p-8 rounded-lg border border-gray-100 text-center my-8">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-4" />
          <h3 className="text-base font-medium mb-1">Error loading event</h3>
          <p className="text-sm text-gray-500 mb-4">There was an error loading the event details.</p>
          <Link to="/dashboard/events">
            <Button className="h-9 text-sm">Back to Events</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-full mx-auto px-4">
        <div className="bg-white p-8 rounded-lg border border-gray-100 text-center my-8">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-4" />
          <h3 className="text-base font-medium mb-1">Event not found</h3>
          <p className="text-sm text-gray-500 mb-4">The event you're looking for doesn't exist or has been removed.</p>
          <Link to="/dashboard/events">
            <Button className="h-9 text-sm">Back to Events</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-2 sm:px-4">
      {/* Header with Back Button */}
      <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-100 mb-3 sm:mb-4">
        <div className="flex items-center gap-1.5 mb-2">
          <Link 
            to="/dashboard/events" 
            className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 text-xs group"
          >
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Back
          </Link>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h1 className="text-base font-medium">{event.title}</h1>
          <div className="flex flex-wrap gap-1.5">
            <Link to={`/dashboard/events/edit/${event.url}`}>
              <Button variant="outline" className="h-8 text-xs px-2">
                <Edit2 className="w-3 h-3 mr-1" />
                Edit
              </Button>
            </Link>
            <Button onClick={() => setIsUploadModalOpen(true)} className="h-8 text-xs px-2">
              <Upload className="w-3 h-3 mr-1" />
              Upload
            </Button>
            <Button 
              variant="outline" 
              className="h-8 text-xs px-2 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-3 sm:space-y-4">
        {/* Stats Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {/* Stats Quick View */}
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden p-3 sm:p-4">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-gray-50 rounded-md p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
                <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                  <Image className="w-4 sm:w-5 h-4 sm:h-5 text-[#FF4444]" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Photos</p>
                  <h3 className="text-lg sm:text-xl font-bold">{event.photo_count}</h3>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-md p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
                <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 sm:w-5 h-4 sm:h-5 text-[#FF4444]" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Time Left</p>
                  <h3 className="text-lg sm:text-xl font-bold">{timeLeftString}</h3>
                </div>
              </div>
            </div>
          </div>
          
          {/* Share Options */}
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden p-3 sm:p-4">
            <h2 className="text-sm sm:text-base font-medium mb-2 sm:mb-3">Share Event</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              <Button 
                variant="outline" 
                onClick={() => setIsQRModalOpen(true)} 
                className="justify-between h-9 sm:h-10 text-xs sm:text-sm"
              >
                <div className="flex items-center">
                  <QrCode className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2" />
                  QR Code
                </div>
                <ChevronRight className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-400" />
              </Button>
              <Button 
                variant="outline" 
                className="justify-between h-9 sm:h-10 text-xs sm:text-sm"
                onClick={handleCopyLink}
              >
                <div className="flex items-center">
                  {linkCopied ? (
                    <>
                      <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2 text-green-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Share2 className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2" />
                      Copy Link
                    </>
                  )}
                </div>
                <ChevronRight className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-400" />
              </Button>
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <h2 className="text-sm sm:text-base font-medium flex items-center gap-2">
              <Grid className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#FF4444]" />
              Photo Gallery
            </h2>
            <Button 
              variant="outline" 
              className={`h-8 sm:h-9 text-xs sm:text-sm px-2 sm:px-3 ${isDownloading ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              onClick={handleDownloadAll}
              disabled={isDownloading || !photos || photos.length === 0}
            >
              {isDownloading ? (
                <div className="flex items-center">
                  <Loader2 className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1 sm:mr-1.5 animate-spin" />
                  Processing...
                </div>
              ) : downloadComplete ? (
                <div className="flex items-center text-green-500">
                  <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1 sm:mr-1.5" />
                  Processing Started
                </div>
              ) : (
                <div className="flex items-center">
                  <Download className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1 sm:mr-1.5" />
                  Download All
                </div>
              )}
            </Button>
          </div>
          
          {downloadComplete && (
            <div className="mx-3 sm:mx-4 mt-3 sm:mt-4 p-2 sm:p-3 bg-green-50 text-green-700 rounded-md flex items-start sm:items-center gap-2 text-xs sm:text-sm border border-green-100">
              <Mail className="w-3.5 sm:w-4 h-3.5 sm:h-4 flex-shrink-0 mt-0.5 sm:mt-0" />
              <p>Download preparation started. You'll receive a notification via email when your ZIP file is ready to download.</p>
            </div>
          )}
          
          <div className="p-3 sm:p-4">
            {isLoadingPhotos ? (
              <div className="h-40 flex items-center justify-center">
                <Loader2 className="w-6 sm:w-8 h-6 sm:h-8 text-[#FF4444] animate-spin" />
              </div>
            ) : !photos || photos.length === 0 ? (
              <div className="text-center py-8">
                <FileImage className="w-10 sm:w-12 h-10 sm:h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4 text-sm">No photos have been uploaded yet</p>
                <Button 
                  className="h-8 sm:h-9 text-xs sm:text-sm"
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  <Upload className="w-3 sm:w-3.5 h-3 sm:h-3.5 mr-1 sm:mr-1.5" />
                  Upload Photos
                </Button>
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-3">Showing {Math.min(visiblePhotos, photos?.length || 0)} of {photos?.length || 0} photos</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
                  {photos && photos.slice(0, visiblePhotos).map((photo) => (
                    <div key={photo.id} className="aspect-square rounded-md overflow-hidden bg-gray-100 group relative">
                      <img
                        src={photo.thumbnail_url}
                        alt={photo.file_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button 
                          className="w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center text-gray-700 hover:text-[#FF4444] transition-colors"
                          onClick={() => handleImageView(photo)}
                        >
                          <Eye className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                        </button>
                        <button 
                          className="w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center text-gray-700 hover:text-[#FF4444] transition-colors"
                          onClick={() => handleDownloadSingle(photo.id)}
                          disabled={isDownloadingOne === photo.id}
                        >
                          {isDownloadingOne === photo.id ? (
                            <Loader2 className="w-3.5 sm:w-4 h-3.5 sm:h-4 animate-spin text-[#FF4444]" />
                          ) : (
                            <Download className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                          )}
                        </button>
                      </div>
                      {photo.is_guest && (
                        <div className="absolute top-1 sm:top-2 right-1 sm:right-2 bg-cyan-500 text-white text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded">
                          Guest
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {visiblePhotos < (photos?.length || 0) && (
                  <div className="mt-4 flex justify-center">
                    <Button 
                      variant="outline" 
                      className="h-8 sm:h-9 text-xs sm:text-sm px-3"
                      onClick={handleLoadMore}
                    >
                      Load More Photos
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Event Details Section */}
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-gray-100">
            <h2 className="text-sm sm:text-base font-medium flex items-center gap-2">
              <Info className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#FF4444]" />
              Event Details
            </h2>
          </div>
          
          <div className="p-3 sm:p-4">
            {/* Event Information */}
            <div className="border border-gray-200 rounded-md p-3 sm:p-4">
              <h3 className="text-xs sm:text-sm font-medium mb-3 sm:mb-4 flex items-center gap-1.5 sm:gap-2">
                <div className="w-5 sm:w-6 h-5 sm:h-6 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                  <Info className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-[#FF4444]" />
                </div>
                Event Information
              </h3>
              <div className="space-y-3 sm:space-y-4">
                {event.description && (
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-8 sm:w-9 h-8 sm:h-9 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                      <Info className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#FF4444]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Description</p>
                      <p className="text-xs sm:text-sm">{event.description}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 sm:w-9 h-8 sm:h-9 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#FF4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="text-xs sm:text-sm">{event.location || 'Not specified'}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 sm:w-9 h-8 sm:h-9 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                    <LinkIcon className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#FF4444]" />
                  </div>
                  <div className="flex-grow">
                    <p className="text-xs text-gray-500">Event URL</p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-1">
                      <p className="text-xs sm:text-sm text-gray-700 font-medium break-all">
                        https://ourphotos.co/e/{event.url}
                      </p>
                      <button 
                        onClick={handleCopyLink}
                        className={`mt-2 sm:mt-0 sm:ml-2 p-1.5 rounded-md transition-colors ${linkCopied ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >
                        {linkCopied ? <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4" /> : <Copy className="w-3.5 sm:w-4 h-3.5 sm:h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 sm:w-9 h-8 sm:h-9 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                    <Lock className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#FF4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Privacy</p>
                    <p className="text-xs sm:text-sm">{event.allow_photo_views ? 'Public Photo Viewing' : 'Private Photo Viewing'}</p>
                    {event.has_password && <p className="text-[10px] sm:text-xs text-amber-600 mt-1">Password Protected</p>}
                  </div>
                </div>
                
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 sm:w-9 h-8 sm:h-9 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                    <Upload className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#FF4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Guest Uploads</p>
                    <p className="text-xs sm:text-sm">{event.allow_guest_uploads ? 'Enabled' : 'Disabled'}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 sm:w-9 h-8 sm:h-9 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#FF4444]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Created At</p>
                    <p className="text-xs sm:text-sm">{formatDate(event.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Image View Modal */}
      {isImageViewOpen && selectedImage && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setIsImageViewOpen(false)}>
          <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="absolute top-2 sm:top-4 right-2 sm:right-4 z-10">
              <button 
                className="w-7 h-7 sm:w-8 sm:h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                onClick={() => setIsImageViewOpen(false)}
              >
                <X className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
              </button>
            </div>
            <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 z-10">
              <button 
                className="w-7 h-7 sm:w-8 sm:h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                onClick={(e) => {
                  e.stopPropagation();
                  // Find the photo with this URL
                  const photo = photos.find(p => p.public_url === selectedImage);
                  if (photo) {
                    handleDownloadSingle(photo.id);
                  } else {
                    // Fallback for direct download if photo object not found
                    try {
                      // Fetch the image and create a blob
                      fetch(selectedImage)
                        .then(response => response.blob())
                        .then(blob => {
                          // Create a blob URL
                          const blobUrl = URL.createObjectURL(blob);
                          
                          // Create temporary anchor element
                          const anchor = document.createElement('a');
                          anchor.href = blobUrl;
                          // Critical: set the download attribute with a filename
                          anchor.setAttribute('download', `photo-${Date.now()}.jpg`);
                          anchor.setAttribute('rel', 'noopener noreferrer');
                          anchor.style.display = 'none';
                          document.body.appendChild(anchor);
                          anchor.click();
                          
                          // Clean up after delay
                          setTimeout(() => {
                            document.body.removeChild(anchor);
                            URL.revokeObjectURL(blobUrl);
                          }, 100);
                        });
                    } catch (error) {
                      console.error('Download failed:', error);
                    }
                  }
                }}
              >
                <Download className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
              </button>
            </div>
            
            {/* Loading Indicator */}
            {isImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 flex items-center gap-2">
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                  <span className="text-sm text-white">Loading image...</span>
                </div>
              </div>
            )}

            <img 
              src={selectedImage} 
              alt="Full size view" 
              className={`w-full h-full object-contain transition-opacity duration-200 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoadStart={() => setIsImageLoading(true)}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-lg overflow-hidden max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 sm:p-4 border-b border-gray-100">
              <h3 className="text-sm sm:text-base font-medium">Delete Event</h3>
            </div>
            <div className="p-3 sm:p-4">
              <div className="flex items-start sm:items-center gap-2 sm:gap-3 mb-3 sm:mb-4 p-2 sm:p-3 bg-red-50 rounded-md border border-red-100">
                <AlertTriangle className="w-4 sm:w-5 h-4 sm:h-5 text-red-600 flex-shrink-0" />
                <p className="text-xs sm:text-sm text-red-700">
                  This action cannot be undone. This will permanently delete the event and all associated photos.
                </p>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                Are you sure you want to delete "{event.title}"?
              </p>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  className="h-8 sm:h-9 text-xs sm:text-sm"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button 
                  className="h-8 sm:h-9 text-xs sm:text-sm bg-red-600 hover:bg-red-700"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <div className="flex items-center">
                      <Loader2 className="w-3 sm:w-3.5 h-3 sm:h-3.5 mr-1 sm:mr-1.5 animate-spin" />
                      Deleting...
                    </div>
                  ) : (
                    'Delete Event'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={isQRModalOpen}
        onClose={() => setIsQRModalOpen(false)}
        title={event.title}
        eventUrl={event.url}
        shareUrl={`https://ourphotos.co/e/${event.url}`}
      />

      {/* Upload Photo Modal */}
      <UploadPhoto
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title={event.title}
        eventUrl={event.url}
        onPhotoUploadComplete={() => {
          // Fotoğrafları yeniden getir
          refetchPhotos();
        }}
      />
    </div>
  );
};

export default EventDetail;